import {Injectable} from '@angular/core';
import {cloneDeep} from 'lodash';

interface ThrottledEndpoint {
    /** The url of the endpoint without the query string, e.g. 'alarms/statistics/active/severitySum' */
    path: string;
    throttleTime: number;
}

interface ThrottledRequest {
    promise: Promise<any>;
    expiresAt: number;
}

/**
 * System wide throttling of expensive GET requests.
 *
 * A request whose url matches one of THROTTLED_ENDPOINTS is sent to the server at most once per throttleTime,
 * no matter how many places in the application ask for it. Every caller in the meanwhile receives a copy of the
 * last response, so the throttling is transparent to them.
 *
 * Throttling is per full url (query string included), therefore requests that differ by their filter are
 * throttled separately and a filter change always reaches the server.
 *
 * Use forceNextRequests() to open the window before it ended, e.g. when the user presses a Refresh button.
 *
 * To throttle another endpoint add it to THROTTLED_ENDPOINTS, e.g.
 *      {path: 'calls/statistics/summary', throttleTime: 7 * ThrottledRequestsService.MINUTE}
 */
@Injectable({
    providedIn: 'root'
})
export class ThrottledRequestsService {

    static readonly MINUTE = 60 * 1000;

    private static readonly THROTTLED_ENDPOINTS: ThrottledEndpoint[] = [
        {path: 'alarms/statistics/active/severitySum', throttleTime: 5 * ThrottledRequestsService.MINUTE},
    ];

    private throttledRequests = new Map<string, ThrottledRequest>();
    private forcedUrls = new Set<string>();

    /**
     * Runs sendRequest, unless the url is throttled and was already sent within its throttling window,
     * in which case the response of that request is returned instead.
     *
     * sendRequest is told to skip the outgoing request deduplication when the window was force released,
     * an identical request that was already outgoing may have been sent before the change that forced it,
     * and its response would then be cached for a whole throttling window.
     */
    run = (url: string, sendRequest: (skipOutgoingDeduplication?: boolean) => Promise<any>): Promise<any> => {
        const throttleTime = this.getThrottleTime(url);

        if (!throttleTime) {
            return sendRequest();
        }

        this.deleteExpired();

        const throttledRequest = this.throttledRequests.get(url);
        if (throttledRequest) {
            return this.copyResponse(throttledRequest.promise);
        }

        const promise = sendRequest(this.forcedUrls.delete(url));
        this.throttledRequests.set(url, {promise, expiresAt: Date.now() + throttleTime});
        // A request that did not reach the server must not hold the window, the next caller should try again
        promise.catch(() => this.throttledRequests.delete(url));

        return this.copyResponse(promise);
    };

    /**
     * Lets the next matching request reach the server even if its throttling window did not end yet.
     * Without urlPart all throttled requests are released.
     */
    forceNextRequests = (urlPart?: string) => {
        this.throttledRequests.forEach((throttledRequest, url) => {
            if (urlPart && !url.includes(urlPart)) {
                return;
            }
            this.throttledRequests.delete(url);
            this.forcedUrls.add(url);
        });
    };

    clear = () => {
        this.throttledRequests.clear();
        this.forcedUrls.clear();
    };

    private getThrottleTime = (url: string): number => {
        const [urlWithoutQueryParams] = (url || '').split('?');

        return ThrottledRequestsService.THROTTLED_ENDPOINTS
            .find(({path}) => urlWithoutQueryParams.endsWith(path))?.throttleTime || 0;
    };

    private deleteExpired = () => {
        const now = Date.now();

        this.throttledRequests.forEach((throttledRequest, url) => {
            throttledRequest.expiresAt <= now && this.throttledRequests.delete(url);
        });
    };

    private copyResponse = (promise: Promise<any>) => promise.then((response) => ({
        ...response,
        data: cloneDeep(response?.data)
    }));
}
